import { UserScopeService, UserScope, restrictToScope, inScope } from '@app/shared/services/user-scope.service';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { LoggerService } from '@app/logger/logger.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { SimplePdfService } from '@app/shared/services/simple-pdf.service';

import { RemissionsService } from '@app/shared/services/entities/remissions.service';
import { DoctorsService } from '@app/shared/services/entities/doctors.service';
import { PlacesService } from '@app/shared/services/entities/places.service';
import { ProductsService } from '@app/shared/services/entities/products.service';
import { ContractsService } from '@app/shared/services/entities/contracts.service';
import { InterventionsService } from '@app/shared/services/entities/interventions.service';
import { UsersService } from '@app/shared/services/entities/users.service';
import { RolesService } from '@app/shared/services/entities/roles.service';
import { WarehousesService } from '@app/shared/services/entities/warehouse.service';
import { StockService } from '@app/shared/services/entities/stock.service';
import { JetstreamService } from '@app/shared/services/jetstream.service';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';

const log = new LoggerService('REMISSION WIZARD');

/**
 * Proceso de creación de remisión (flujo "Salida de material · remisión" del
 * forms de Medallia, compacto) dentro del módulo de Salida de material:
 *
 *   1. Técnico Medtronic     (radial; fijo si el usuario actual es técnico)
 *   2. Médico y cirugía      (folio de cirugía opcional que prellena datos)
 *   3. Material              (IGUAL que el folio: RFID por widget / bandeja /
 *                             anaquel / pistola contra el inventario; cada
 *                             pieza queda apartada en Transito, con su precio
 *                             de contrato y bandera sin cargo; sección Otros)
 *   4. Comentarios           (notas y resumen de la captura)
 *   5. PDF                   (documento con folio REM y QR; guardar o emitir)
 *
 * Los borradores (status Incompleto) se retoman desde la lista de Salida de
 * material (/interventions/remission/:id). La consulta histórica vive en
 * Reportes.
 */
import { isValidRfidTag, RFID_TAG_LENGTH } from '@app/shared/utils/rfid';
@Component({
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MaterialModule, DecimalPipe, AutoSelectComponent],
  selector: 'app-remission-wizard',
  templateUrl: './remission-wizard.component.html',
  styleUrls: ['../interventions-wizard/interventions-wizard.component.scss', './remission-wizard.component.scss'],
})
export class RemissionWizardComponent implements OnInit {
  public step = 0;
  public readonly stepTitles = ['Técnico, hospital y médico', 'Folio de cirugía', 'Material', 'Comentarios', 'PDF'];

  public pdfGenerated = false;

  public record: any = null; // remisión existente cuando se retoma un borrador

  public form!: FormGroup;

  public technicianList: any[] = [];
  public technicianLocked = false;
  public technicianLockedName = '';

  public doctorsList: any[] = [];
  public placesList: any[] = [];
  /** Hospitales asignados al usuario (null = ve todo). */
  public scope: UserScope = { places: null, specialties: null };
  private userScopeService = inject(UserScopeService);

  /** Hospitales/clientes que puede elegir el usuario (conserva el del
   *  borrador que se retoma). */
  public get scopedPlaces(): any[] {
    const current = this.form?.get('place')?.value;
    // Solo sitios tipo Hospital (no Macrocentro ni almacenes) y ACTIVOS,
    // dentro del alcance del usuario.
    return this.placesList.filter(p => p?.id === current
      || (String(p?.type ?? '').toUpperCase() === 'HOSPITAL'
          && (p?.active === true || String(p?.active).toLowerCase() === 'true')
          && inScope(this.scope.places, p?.id, false)));
  }
  public productsList: any[] = [];
  public productsObject: { [id: string]: any } = {};
  public interventionsList: any[] = [];
  public contract: any = null;

  // ── Material (igual que el folio: piezas del inventario vía RFID) ──
  public warehousesList: any[] = [];
  public warehouseOptions: any[] = [];
  public selectedWarehouse: string | null = null;
  public scanValue = '';
  public rowAdd: any = { product: null, batch: null, quantity: 1 };
  public rfidMode: 'widget' | 'bandeja' | 'anaquel' | 'pistola' = 'widget';
  public deviceName = '';
  public reading = false;
  public readTags: { epc: string; stockItem: any | null; selected: boolean; already: boolean }[] = [];

  /**
   * Una línea por PIEZA apartada: { stock, product, code, description, batch,
   * expiration, tagRfid, quantity:1, unitPrice, noCharge, reason, sinPrecio }.
   */
  public lines: any[] = [];
  /** Piezas que ya venían apartadas en el borrador (no se liberan al salir). */
  private originalStockIds = new Set<string>();

  public otherItems: { cfn: string | null; pieces: number | null; batch: string | null }[] = [];
  public otherAdd: any = { cfn: null, pieces: 1, batch: null };

  // ── Etiquetas de los autocompletes ──
  /** El hospital se muestra por su ALIAS (catálogo de Sitios); si no tiene, por su nombre. */
  public placeAliasLabel = (place: any) => String(place?.alias || place?.name || '');
  public productLabel = (product: any) => `${product?.code ?? ''} — ${product?.description ?? ''}`;
  public interventionLabel = (record: any) =>
    `${record?.fileNumber ?? ''}${record?.patientName ? ' · ' + record.patientName : ''}`;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private customMessageService: CustomMessageService,
    private simplePdf: SimplePdfService,
    private remissionsService: RemissionsService,
    private doctorsService: DoctorsService,
    private placesService: PlacesService,
    private productsService: ProductsService,
    private contractsService: ContractsService,
    private interventionsService: InterventionsService,
    private usersService: UsersService,
    private rolesService: RolesService,
    private warehousesService: WarehousesService,
    private stockService: StockService,
    private jetstreamService: JetstreamService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.form = this.formBuilder.group({
      // Folio de la remisión: campo abierto que captura el técnico.
      folio: [null, Validators.required],
      technician: [null, Validators.required],
      intervention: [null],
      place: [null, Validators.required],
      doctor: [null],
      notes: [null],
    });
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Cargando catálogos...', loading: true });
    try {
      await Promise.all([this.loadCatalogs(), this.loadTechnicians()]);
      const id = this.route.snapshot.paramMap.get('id');
      if (id) await this.resumeDraft(id);
      this.customMessageService.close();
    } catch (err) {
      log.error('carga inicial', err);
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  private async loadCatalogs(): Promise<void> {
    this.scope = await this.userScopeService.load();
    const [doctors, places, products, contracts, interventions, warehouses] = await Promise.all([
      this.doctorsService.search({ searchType: 'default', condition: { deleted: false } }),
      this.placesService.search({ searchType: 'default', condition: { deleted: false } }),
      this.productsService.search({ searchType: 'default', condition: { deleted: false } }),
      this.contractsService.search({ searchType: 'default', condition: { deleted: false } }),
      this.interventionsService.search({ searchType: 'default', condition: { deleted: false, status: ['Pendiente', 'Permiso', 'Borrador', 'Incompleto'] } }),
      this.warehousesService.search({ searchType: 'default', condition: { deleted: false } }),
    ]);
    this.doctorsList = ((doctors.data as any[]) ?? []).filter(x => x);
    this.placesList = ((places.data as any[]) ?? []).filter(x => x);
    this.productsList = ((products.data as any[]) ?? []).filter(x => x)
      .sort((a, b) => String(a.code).localeCompare(String(b.code), 'es'));
    this.productsObject = this.productsList.reduce((r, a) => { r[a.id] = a; return r; }, Object.create(null));
    this.contract = ((contracts.data as any[]) ?? []).find(c => c && (c.status === true || c.status === 'true')) ?? null;
    this.interventionsList = ((interventions.data as any[]) ?? []).filter(x => x?.fileNumber)
      .sort((a, b) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
    this.warehousesList = ((warehouses.data as any[]) ?? []).filter(x => x);
  }

  private async loadTechnicians(): Promise<void> {
    const normalize = (name: any) =>
      String(name ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
    const [rolesResponse, usersResponse, me] = await Promise.all([
      this.rolesService.search({ searchType: 'default', condition: { deleted: false } }),
      this.usersService.search({ searchType: 'default', condition: { deleted: false } }),
      this.usersService.loadCurrentUser(),
    ]);
    const techRoleIds = new Set(
      ((rolesResponse.data as any[]) ?? []).filter(r => r && normalize(r.name) === 'tecnico').map(r => r.id),
    );
    this.technicianList = ((usersResponse.data as any[]) ?? [])
      .filter(u => u && u.role && techRoleIds.has(u.role) && u.active !== false)
      .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), 'es'));
    if (me && me.role && techRoleIds.has(me.role)) {
      this.technicianLocked = true;
      this.technicianLockedName = me.name ?? '';
      if (!this.technicianList.some(u => u.id === me.id)) this.technicianList.push(me);
      this.form.get('technician')?.setValue(me.id);
      this.form.get('technician')?.disable();
    }
  }

  /** Retomar un borrador de remisión desde la lista de Salida de material. */
  private async resumeDraft(id: string): Promise<void> {
    const response = await this.remissionsService.search({ searchType: 'default', condition: { deleted: false, id } });
    const record = (response.data as any[])?.[0];
    if (!record) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: 'No se encontró el borrador de remisión.', dismissBtn: true });
      this.router.navigate(['/interventions/remissions']);
      return;
    }
    this.record = record;
    // El médico se guardó por nombre: se re-liga al catálogo por nombre.
    const doctor = this.doctorsList.find(d => String(d.name ?? '').trim() === String(record.doctorName ?? '').trim());
    this.form.patchValue({
      technician: record.technician ?? this.form.get('technician')?.value ?? null,
      intervention: record.intervention ?? null,
      place: record.place ?? null,
      doctor: doctor?.id ?? null,
      notes: record.notes ?? null,
      folio: record.folio ?? null,
    });
    this.onPlaceChange();
    this.selectedWarehouse = record.warehouse ?? this.selectedWarehouse;
    // Cada partida guardada regresa como línea; sus piezas ya estaban
    // apartadas, así que NO se vuelven a tocar ni se liberan al salir.
    this.lines = (record.items ?? []).map((item: any) => ({ ...item, sinPrecio: false }));
    this.originalStockIds = new Set(this.lines.map(l => l.stock).filter(Boolean));
    this.otherItems = Array.isArray(record.otherItems) ? [...record.otherItems] : [];
  }

  // ─── Paso 2: al ligar un folio se jalan sus datos principales ───────────

  onInterventionChange(): void {
    const interventionId = this.form.get('intervention')?.value;
    const record = this.interventionsList.find(i => i.id === interventionId);
    if (!record) return;
    if (record.hospital) { this.form.get('place')?.setValue(record.hospital); this.onPlaceChange(); }
    if (record.doctor && this.doctorsList.some(d => d.id === record.doctor)) {
      this.form.get('doctor')?.setValue(record.doctor);
    }
    if (record.technician && !this.technicianLocked
        && this.technicianList.some(t => t.id === record.technician)) {
      this.form.get('technician')?.setValue(record.technician);
    }
  }

  /** Almacenes del hospital/cliente elegido (igual que en el folio). */
  onPlaceChange(): void {
    const place = this.form.get('place')?.value;
    // Si cambia el hospital, el folio y el médico de otro hospital ya no aplican.
    const intervention = this.interventionsList.find(i => i.id === this.form.get('intervention')?.value);
    if (intervention && place && (intervention.hospital?.id ?? intervention.hospital) !== place) {
      this.form.get('intervention')?.setValue(null);
    }
    const doctor = this.doctorsList.find(d => d.id === this.form.get('doctor')?.value);
    const doctorPlaces: string[] = Array.isArray(doctor?.places) && doctor.places.length ? doctor.places : (doctor?.place ? [doctor.place] : []);
    if (doctor && place && doctorPlaces.length && !doctorPlaces.includes(place)) {
      this.form.get('doctor')?.setValue(null);
    }
    // Solo lockers tipo LOCKER (los Inhouse no se usan para sacar material);
    // se conserva el ya elegido en un borrador para no perderlo.
    this.warehouseOptions = this.warehousesList.filter(w => w.place === place
      && (w.storageType !== 'INHOUSE' || w.id === this.selectedWarehouse));
    if (this.warehouseOptions.length === 1 && this.lines.length === 0) {
      this.selectedWarehouse = this.warehouseOptions[0].id;
    }
  }

  /** Folios de cirugía del hospital elegido (conserva el ya ligado). */
  public get interventionsOfPlace(): any[] {
    const placeId = this.form?.get('place')?.value;
    const current = this.form?.get('intervention')?.value;
    if (!placeId) return this.interventionsList;
    return this.interventionsList.filter(i => i?.id === current || (i?.hospital?.id ?? i?.hospital) === placeId);
  }

  public get doctorsOfPlace(): any[] {
    const placeId = this.form?.get('place')?.value;
    if (!placeId) return this.doctorsList;
    // Solo por hospital (no por especialidad). `places` es la lista nueva
    // del catálogo de Médicos; `place` el campo viejo de un solo hospital.
    const current = this.form?.get('doctor')?.value;
    return this.doctorsList.filter(d => {
      if (d?.id === current) return true;
      const places: string[] = Array.isArray(d?.places) && d.places.length ? d.places : (d?.place ? [d.place] : []);
      return places.length === 0 || places.includes(placeId);
    });
  }

  // ─── Paso 3: material — mismas lecturas RFID que el folio ───────────────

  private priceFor(product: any): number | null {
    if (!this.contract?.prices || !product) return null;
    const row = (this.contract.prices as any[]).find(pr =>
      (product.itemNumber && String(pr.itemNumber) === String(product.itemNumber)) ||
      (pr.description && product.description && String(pr.description).trim().toUpperCase() === String(product.description).trim().toUpperCase()));
    const price = row ? Number(row.price) : NaN;
    return isNaN(price) ? null : price;
  }

  /** Valida y aparta una pieza (Transito), agregándola como línea con precio. */
  private async reserveAndAdd(stockItem: any): Promise<boolean> {
    if (this.lines.some(l => l.stock === stockItem.id)) return false;
    if (stockItem.status !== 'Disponible') {
      await this.customMessageService.open({
        title: 'Información', type: 'neutral',
        message: `La pieza tiene estatus <b>${stockItem.status}</b>: no se puede cargar. Si está en Transito, ya la tiene otro vale o remisión.`,
        dismissBtn: true,
      });
      return false;
    }
    if (stockItem.warehouse !== this.selectedWarehouse) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'La pieza pertenece a OTRO locker.', dismissBtn: true });
      return false;
    }
    const response: any = await this.stockService.update({ id: stockItem.id, tagRfid: stockItem.tagRfid, status: 'Transito' });
    if (response.state !== 'success') return false;

    const product = this.productsObject[stockItem.product];
    const price = this.priceFor(product);
    this.lines.push({
      stock: stockItem.id,
      product: stockItem.product,
      code: product?.code ?? stockItem.product,
      description: product?.description ?? '—',
      batch: stockItem.batch ?? null,
      expiration: stockItem.expiration ?? null,
      tagRfid: stockItem.tagRfid ?? null,
      quantity: 1,
      unitPrice: price ?? 0,
      noCharge: false,
      reason: null,
      sinPrecio: price === null,
    });
    return true;
  }

  /** Widget: lectura uno a uno (el lector escribe la etiqueta y da Enter). */
  async scanTag(): Promise<void> {
    const tag = this.scanValue?.trim().toUpperCase();
    this.scanValue = '';
    if (!tag) return;
    if (!isValidRfidTag(tag)) {
      await this.customMessageService.open({ title: 'Etiqueta inválida', type: 'warning', message: `La etiqueta <b>${tag}</b> tiene ${tag.length} caracteres; debe tener exactamente ${RFID_TAG_LENGTH}.`, dismissBtn: true });
      return;
    }
    if (this.lines.some(l => String(l.tagRfid ?? '').toUpperCase() === tag)) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: `La etiqueta <b>${tag}</b> ya está en la lista.`, dismissBtn: true });
      return;
    }
    try {
      const response = await this.stockService.search({ searchType: 'default', condition: { deleted: false, tagRfid: tag } });
      const stockItem = (response.data as any[])?.[0];
      if (!stockItem) {
        await this.customMessageService.open({ title: 'Error', type: 'error', message: `Etiqueta <b>${tag}</b> no encontrada en el inventario.`, dismissBtn: true });
        return;
      }
      await this.reserveAndAdd(stockItem);
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  /** Piezas sin etiqueta: se agregan por renglón (producto + lote + cantidad). */
  async addByRow(): Promise<void> {
    const { product, batch, quantity } = this.rowAdd;
    if (!product || !quantity || quantity < 1) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Elige insumo y cantidad.', dismissBtn: true });
      return;
    }
    try {
      const condition: any = { deleted: false, warehouse: this.selectedWarehouse, product, status: ['Disponible'] };
      if (batch) condition['batch'] = batch;
      const response = await this.stockService.search({ searchType: 'default', condition });
      const candidates = ((response.data as any[]) ?? [])
        .filter(x => x && !x.tagRfid && !this.lines.some(l => l.stock === x.id));
      if (candidates.length < quantity) {
        await this.customMessageService.open({
          title: 'Información', type: 'neutral',
          message: `Solo hay <b>${candidates.length}</b> piezas sin etiqueta disponibles de ese renglón (pediste ${quantity}).`,
          dismissBtn: true,
        });
        if (candidates.length === 0) return;
      }
      let added = 0;
      for (const item of candidates.slice(0, quantity)) {
        if (await this.reserveAndAdd(item)) added++;
      }
      this.rowAdd = { product: null, batch: null, quantity: 1 };
      if (added > 0) {
        await this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: `${added} piezas apartadas.`, duration: 1500 });
      }
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  /** Al cambiar de modo se propone el dispositivo del almacén seleccionado. */
  public onRfidModeChange(): void {
    this.readTags = [];
    if (this.rfidMode === 'widget') return;
    const warehouse = this.warehousesList.find(w => w.id === this.selectedWarehouse);
    if (warehouse?.rfidDevice && !this.deviceName) this.deviceName = warehouse.rfidDevice;
  }

  /** Extrae EPCs de las distintas formas de respuesta de Jetstream. */
  private extractEpcs(payload: any): string[] {
    const out = new Set<string>();
    const walk = (node: any): void => {
      if (!node) return;
      if (typeof node === 'string') { if (node.trim()) out.add(node.trim().toUpperCase()); return; }
      if (Array.isArray(node)) { node.forEach(walk); return; }
      if (typeof node === 'object') {
        for (const key of ['EPCList', 'EpcList', 'epclist', 'EPCs', 'Epcs', 'Tags', 'TagList']) {
          if (node[key]) walk(node[key]);
        }
        for (const key of ['EPC', 'Epc', 'epc', 'TagId', 'Value']) {
          if (typeof node[key] === 'string') walk(node[key]);
        }
        if (node.Events) walk(node.Events);
      }
    };
    walk(payload);
    return [...out];
  }

  /** Lee bandeja / anaquel / pistola y cruza contra el inventario del almacén. */
  async readDevice(): Promise<void> {
    if (this.rfidMode !== 'pistola' && !this.deviceName.trim()) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Indica el nombre del dispositivo lector.', dismissBtn: true });
      return;
    }
    this.reading = true;
    this.readTags = [];
    try {
      const payload = this.rfidMode === 'pistola'
        ? await this.jetstreamService.scanning(this.deviceName.trim())
        : await this.jetstreamService.scan(this.deviceName.trim());
      const epcs = this.extractEpcs(payload);
      if (epcs.length === 0) {
        await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'El dispositivo no reportó etiquetas.', dismissBtn: true });
        return;
      }
      const response = await this.stockService.search({
        searchType: 'default',
        condition: { deleted: false, warehouse: this.selectedWarehouse, tagRfid: epcs },
      });
      const byTag = new Map(((response.data as any[]) ?? []).filter(x => x?.tagRfid)
        .map(x => [String(x.tagRfid).toUpperCase(), x]));
      this.readTags = epcs.map(epc => {
        const stockItem = byTag.get(epc) ?? null;
        const already = !!stockItem && this.lines.some(l => l.stock === stockItem.id);
        return {
          epc,
          stockItem,
          already,
          selected: !!stockItem && !already && stockItem.status === 'Disponible',
        };
      });
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    } finally {
      this.reading = false;
    }
  }

  public get selectableTags(): number {
    return this.readTags.filter(t => t.stockItem && !t.already).length;
  }

  public toggleAllTags(value: boolean): void {
    for (const tag of this.readTags) {
      if (tag.stockItem && !tag.already) tag.selected = value;
    }
  }

  async addSelectedTags(): Promise<void> {
    const chosen = this.readTags.filter(t => t.selected && t.stockItem && !t.already);
    if (chosen.length === 0) {
      await this.customMessageService.open({ title: 'Información', type: 'neutral', message: 'No hay etiquetas seleccionadas por cargar.', dismissBtn: true });
      return;
    }
    let added = 0;
    for (const tag of chosen) {
      if (await this.reserveAndAdd(tag.stockItem)) { added++; tag.already = true; tag.selected = false; }
    }
    if (added > 0) {
      await this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: `${added} piezas apartadas de la lectura.`, duration: 1500 });
    }
  }

  /** Quitar una línea regresa su pieza a Disponible. */
  async removeLine(index: number): Promise<void> {
    const line = this.lines[index];
    if (!line) return;
    const confirmed = await this.customMessageService.open({
      title: 'Cuidado', type: 'warning',
      message: `¿Quitar la pieza <b>${line.code}</b> (${line.tagRfid || 'sin etiqueta'})? Regresará a Disponible.`,
      dismissBtn: true, acceptBtn: true,
    });
    if (!confirmed) return;
    try {
      if (line.stock) {
        await this.stockService.update({ id: line.stock, tagRfid: line.tagRfid, status: 'Disponible' });
        this.originalStockIds.delete(line.stock);
      }
      this.lines.splice(index, 1);
    } catch (err) {
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  public get linesWithoutPrice(): boolean {
    return this.lines.some(l => l.sinPrecio && !l.noCharge);
  }

  public get total(): number {
    return this.lines.reduce((sum, l) => sum + (l.noCharge ? 0 : (Number(l.unitPrice) || 0) * (Number(l.quantity) || 0)), 0);
  }

  /** Cantidades siempre enteras (sin decimales); el precio sí lleva decimales. */
  public toInt(value: any, minimum = 0): number {
    const n = Math.floor(Number(value) || 0);
    return Math.max(minimum, n);
  }

  addOther(): void {
    if (!this.otherAdd.cfn || !this.otherAdd.pieces || this.otherAdd.pieces < 1) return;
    this.otherItems.push({ cfn: String(this.otherAdd.cfn).trim().toUpperCase(), pieces: this.toInt(this.otherAdd.pieces, 1), batch: this.otherAdd.batch || null });
    this.otherAdd = { cfn: null, pieces: 1, batch: null };
  }

  removeOther(index: number): void {
    this.otherItems.splice(index, 1);
  }

  // ─── Guardar / emitir ───────────────────────────────────────────────────

  private async validateCapture(): Promise<boolean> {
    const folioCtrl = this.form.get('folio');
    folioCtrl?.setValue(String(folioCtrl?.value ?? '').trim() || null, { emitEvent: false });
    if (this.form.invalid) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Captura el folio de la remisión y elige el técnico Medtronic y el hospital/cliente.', dismissBtn: true });
      return false;
    }
    if (await this.remissionFolioTaken(folioCtrl!.value)) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: `El folio de remisión <b>${folioCtrl!.value}</b> ya existe. Captura otro folio.`, dismissBtn: true });
      return false;
    }
    if (this.lines.length === 0 && this.otherItems.length === 0) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: 'Carga al menos una pieza (o un renglón en Otros).', dismissBtn: true });
      return false;
    }
    return true;
  }

  /** true si otra remisión (no esta) ya usa ese folio. */
  private async remissionFolioTaken(folio: string): Promise<boolean> {
    try {
      const r: any = await this.remissionsService.search({ searchType: 'default', condition: { deleted: false, folio } });
      return ((r?.data as any[]) ?? []).some(x => x?.id !== this.record?.id
        && String(x?.folio ?? '').trim().toUpperCase() === folio.toUpperCase());
    } catch { return false; }
  }

  /** Guarda (create o update) y actualiza this.record. Lanza en error. */
  private async persistCore(status: 'Incompleto' | 'Emitida'): Promise<any> {
    const formValue: any = this.form.getRawValue();
    const doctor = this.doctorsList.find(d => d.id === formValue.doctor);
    const payload: any = {
      folio: formValue.folio,
      place: formValue.place,
      contract: this.contract?.id ?? null,
      doctorName: doctor?.name ?? null,
      technician: formValue.technician ?? null,
      intervention: formValue.intervention ?? null,
      warehouse: this.selectedWarehouse ?? null,
      notes: formValue.notes,
      status,
      items: this.lines.map(({ sinPrecio, ...line }) => line),
      otherItems: this.otherItems,
    };
    const response: any = this.record?.id
      ? await this.remissionsService.update({ id: this.record.id, ...payload })
      : await this.remissionsService.create(payload);
    if (response.state !== 'success') throw new Error(response.message);
    this.record = response.data?.[0] ?? this.record;
    // Una vez guardadas, las piezas pertenecen al borrador (no se liberan al salir).
    this.originalStockIds = new Set(this.lines.map(l => l.stock).filter(Boolean));
    return this.record;
  }

  private async persist(status: 'Incompleto' | 'Emitida'): Promise<void> {
    if (!(await this.validateCapture())) return;
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Guardando remisión...', loading: true });
    try {
      await this.persistCore(status);
      this.customMessageService.close();
      await this.customMessageService.open({
        title: '¡Hecho!', type: 'success',
        message: `Remisión <b>${this.record?.folio ?? ''}</b> ${status === 'Emitida' ? 'emitida' : 'guardada como borrador (Incompleto)'}.`,
        duration: 2200,
      });
      this.router.navigate(['/interventions/remissions']);
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  saveDraft(): void { void this.persist('Incompleto'); }
  saveAndEmit(): void { void this.persist('Emitida'); }

  /**
   * Salir SIN guardar: las piezas apartadas en ESTA sesión (que no venían de
   * un borrador guardado) regresan a Disponible.
   */
  async cancel(): Promise<void> {
    const newPieces = this.lines.filter(l => l.stock && !this.originalStockIds.has(l.stock));
    if (newPieces.length > 0) {
      const confirmed = await this.customMessageService.open({
        title: 'Cuidado', type: 'warning',
        message: `Vas a salir <b>sin guardar</b>: las ${newPieces.length} piezas apartadas en esta sesión regresarán a Disponible.`,
        dismissBtn: true, acceptBtn: true, acceptBtnText: 'Salir', dismissBtnText: 'Volver',
      });
      if (!confirmed) return;
      try {
        await this.stockService.update({
          operationType: 'multiple',
          records: newPieces.map(l => ({ id: l.stock, tagRfid: l.tagRfid, status: 'Disponible' })),
        });
      } catch (err) {
        log.error('liberando piezas', err);
      }
    }
    this.router.navigate(['/interventions/remissions']);
  }

  // ─── Paso 5: PDF ────────────────────────────────────────────────────────

  private catalogName(list: any[], id: any): string {
    return list.find(x => x?.id === id)?.name ?? '—';
  }

  /**
   * Genera el PDF de la remisión. Si todavía no existe el registro, primero
   * se guarda como borrador (Incompleto) para tener folio REM y QR.
   */
  async generatePdf(): Promise<void> {
    if (!(await this.validateCapture())) return;
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Preparando PDF...', loading: true });
    try {
      if (!this.record?.id) await this.persistCore('Incompleto');
      const formValue: any = this.form.getRawValue();
      const intervention = this.interventionsList.find(i => i.id === formValue.intervention);
      const technicianName = this.technicianLocked
        ? this.technicianLockedName
        : this.catalogName(this.technicianList, formValue.technician);

      const rows: any[] = this.lines.map(line => ([
        line.code ?? '—',
        line.description ?? '—',
        String(line.quantity ?? 1),
        line.batch ?? '—',
        line.tagRfid ?? 'Sin etiqueta',
        line.noCharge ? 'Sin cargo' : String(line.unitPrice ?? 0),
        line.noCharge ? (line.reason ?? '—') : '—',
      ]));
      for (const other of this.otherItems) {
        rows.push([other.cfn ?? '—', 'Otros — fuera de catálogo', String(other.pieces ?? 0), other.batch ?? '—', '—', '—', '—']);
      }

      await this.simplePdf.simplePDF({
        fileName: `Remision-${this.record?.folio ?? 'nueva'}`,
        sections: [
          { type: 'header', header: { title: `Remisión — ${this.record?.folio ?? ''}`, subtitle: 'Documento comercial de material entregado. Firmas al calce.' } },
          { type: 'raw', content: {
              columns: [
                { width: '*', stack: [
                  { text: `Fecha: ${this.record?.createdOn ?? ''}`, margin: [0, 2] },
                  { text: `Hospital: ${this.catalogName(this.placesList, formValue.place)}`, margin: [0, 2] },
                  { text: `Médico: ${this.catalogName(this.doctorsList, formValue.doctor)}`, margin: [0, 2] },
                  { text: `Técnico: ${technicianName}`, margin: [0, 2] },
                  { text: `Folio de cirugía: ${intervention?.fileNumber ?? 'Remisión directa, sin cirugía'}`, margin: [0, 2] },
                  ...(formValue.notes ? [{ text: `Comentarios: ${formValue.notes}`, margin: [0, 2] }] : []),
                ]},
                // El QR trae el folio REM para identificar el documento firmado.
                { width: 110, qr: this.record?.folio ?? 'REM', fit: 100 },
              ],
              margin: [0, 8, 0, 8],
          }},
          { type: 'raw', content: {
              table: {
                headerRows: 1,
                widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
                body: [
                  ['Código / CFN', 'Descripción', 'Cantidad', 'Lote', 'Etiqueta RFID', 'Precio unit.', 'Motivo sin cargo'],
                  ...rows,
                ],
              },
              fontSize: 8,
          }},
          { type: 'raw', content: { text: `Total: ${this.total.toFixed(2)}`, alignment: 'right', bold: true, margin: [0, 8, 0, 0] } },
          { type: 'raw', content: {
              columns: [
                { stack: [{ text: '\n\n_____________________________' }, { text: 'Técnico' }], alignment: 'center' },
                { stack: [{ text: '\n\n_____________________________' }, { text: 'Recibió' }], alignment: 'center' },
              ],
              margin: [0, 24, 0, 0],
          }},
        ],
      } as any);
      this.pdfGenerated = true;
      this.customMessageService.close();
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    }
  }

  goToStep(index: number): void { this.step = index; }
}
